/* eslint-disable no-underscore-dangle */

import { ApolloClient, HttpLink, ApolloLink } from '@apollo/client/core';
import { setContext } from '@apollo/link-context';
import { InMemoryCache } from '@apollo/client/cache';
import possibleTypes from '@/schemas/possibleTypes.json';
import fetch from 'isomorphic-unfetch';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { sha256 } from 'crypto-hash';
import LocationService from '@/services/LocationService';

let apolloClient;
let cacheValue;


export function includeCacheControlHeader(include) {
 if(include  && (include ==='nocache' || include ==='invalidate')) {
   cacheValue = include;
 }
}

let traceId = '';
let requestId = '';

export function setTraceId(value) {
  traceId = value;
}
export function setRequestId(value) {
  requestId = value;
}

export function refreshApolloClient({ restoreCache = true, ssr = false, sessionid = '', customertoken = '' } = {}) {
  const authLink = setContext((_, { headers }) => {
    const newHeaders = {
      ...headers,
    };

    if (sessionid) {
      newHeaders.sessionid = sessionid;
    }

    if (customertoken) {
      newHeaders.customertoken = customertoken;
    }

    if (cacheValue) {
      newHeaders['article-cache-control'] = cacheValue;
    }

    if (LocationService.getCurrencyId()) {
      newHeaders.currency = LocationService.getCurrencyId();
    }

    if (traceId) {
      newHeaders['x-amzn-trace-id'] = traceId;
    }

    if (requestId) {
      newHeaders['x-request-id'] = requestId;
    }

    return {
      headers: newHeaders,
    };
  });

  function getApolloUrl() {
    if (process.env.APOLLO_LOCAL_ENDPOINT) {
      return process.env.APOLLO_LOCAL_ENDPOINT;
    }

    if (import.meta.env.SSR) {
      return import.meta.env.VITE_APOLLO_SERVER_ENDPOINT;
    }

    return import.meta.env.VITE_APOLLO_CLIENT_ENDPOINT;
  }

  // do need this from time to time. will leave it in
  const interceptedFetch = async (...args) => {
    const result = await fetch(...args);
    const clone = result.clone();
    const iterateHeaders = [];
    clone.headers.forEach((value, key) => iterateHeaders.push({ key, value }));
    logger.debug({
      msg: 'Apollo Http Intercept',
      request: args,
      responseHeaders: iterateHeaders,
      result: await clone.json(),
    });
    return result;
  };

  const httpLink = new HttpLink({
    uri: getApolloUrl(),
    credentials: 'include',
    fetch: fetch,
  });

  const cache = new InMemoryCache({
    possibleTypes,
    typePolicies: {
      Query: {
        fields: {
          commonData: {
            merge: true,
          },
          confirmPurchase: {
            merge: true,
          },
        },
      },
      OrderDiscount: {
        keyFields: false,
      },
      ProductTaxonomyAttributeGroup: {
        keyFields: false,
      },
      ProductTaxonomyAttribute: {
        keyFields: false,
      },
      ProductTaxonomyCategory: {
        keyFields: false,
      },
    },
  });

  if (restoreCache && !import.meta.env.SSR) {
    const state = window.__APOLLO_STATE__;
    if (state) {
      cache.restore(state);
    }
  }

  apolloClient = new ApolloClient({
    link: ApolloLink.from([
      createPersistedQueryLink({
        useGETForQueries: true,
        useGETForHashedQueries: true,
        sha256,
      }),
      authLink,
      httpLink,
    ]),
    cache,
    ...(ssr ? { ssrMode: true } : { ssrForceFetchDelay: 100 }),
    name: `storefront-${ssr ? 'ssr' : 'client'}`,
    version: process.env.COMMIT_HASH,
  });
}

// eslint-disable-next-line import/prefer-default-export
export function createApolloClient(ssr = false) {
  if (apolloClient) {
    return apolloClient;
  }
  refreshApolloClient({
    restoreCache: true,
    ssr,
  });

  return apolloClient;
}
